// Pure helpers that turn a VIP credit-note parse (POST /credit-note/parse,
// vip#421) into form values, invoice ticks, warnings and ack-payload fields.
// The parse is advisory: nothing here blocks submission.

const VALUE_TOLERANCE = 1; // rupees; the document and the expected amount round differently

const present = (v) => v != null && String(v).trim() !== "";

const normalizeInvoiceNo = (s) => String(s ?? "").trim().toLowerCase();

// Form values the parse can fill, keyed by form field. Absent values are omitted
// so they never blank out what the vendor already has.
export function parsedFormValues(parsed) {
  const out = {};
  if (!parsed) return out;
  if (present(parsed.credit_note_number)) out.cnNumber = String(parsed.credit_note_number).trim();
  if (Number.isFinite(parsed.credit_note_total_value)) out.cnValue = parsed.credit_note_total_value.toFixed(2);
  if (/^\d{4}-\d{2}-\d{2}$/.test(parsed.credit_note_date ?? "")) out.cnDate = parsed.credit_note_date;
  return out;
}

// The FINS invoice_numbers (the checkbox keys) of the invoices VIP matched.
export function invoicesToTick(matchedInvoices, invoices) {
  const wanted = new Set((matchedInvoices || []).map((m) => normalizeInvoiceNo(m?.invoice_no)));
  return new Set(
    (invoices || [])
      .map((inv) => inv?.invoice_number)
      .filter((no) => no != null && wanted.has(normalizeInvoiceNo(no))),
  );
}

// Warnings shown next to the form; each is null when there is nothing to say.
export function parseWarnings(parse, { poCode, expectedAmount } = {}) {
  if (!parse) return { po: null, invoices: null, value: null };
  const parsed = parse.parsed || {};
  const po =
    parse.po_number_matches === false
      ? `This credit note is for PO ${parsed.po_number}; you are on ${poCode}.`
      : null;
  const unmatched = parse.unmatched_invoice_references || [];
  const invoices = unmatched.length
    ? `Not on this PO or not approved: ${unmatched.join(", ")}. Tick the right invoices yourself.`
    : null;
  const total = parsed.credit_note_total_value;
  const value =
    Number.isFinite(total) && Number.isFinite(expectedAmount) && expectedAmount > 0 &&
    Math.abs(total - expectedAmount) > VALUE_TOLERANCE
      ? `The credit note total differs from the expected amount (${expectedAmount.toFixed(2)}).`
      : null;
  return { po, invoices, value };
}

// Read-only summary rows for the fields card.
export function parseSummary(parsed) {
  if (!parsed) return [];
  const rows = [];
  const money = (v) => (Number.isFinite(v) ? v.toFixed(2) : null);
  if (money(parsed.taxable_value)) rows.push(["Taxable value", money(parsed.taxable_value)]);
  const taxParts = [
    ["CGST", parsed.cgst_amount],
    ["SGST", parsed.sgst_amount],
    ["IGST", parsed.igst_amount],
  ].filter(([, v]) => Number.isFinite(v)).map(([k, v]) => `${k} ${v.toFixed(2)}`);
  if (money(parsed.total_tax_amount)) {
    rows.push(["Tax", taxParts.length ? `${money(parsed.total_tax_amount)} (${taxParts.join(", ")})` : money(parsed.total_tax_amount)]);
  }
  if (present(parsed.hsn_sac)) rows.push(["HSN/SAC", parsed.hsn_sac]);
  if (present(parsed.seller_gstin)) rows.push(["Seller GSTIN", parsed.seller_gstin]);
  return rows;
}

// Extra credit_note fields for the PO-ack payload. Only what the document
// printed is sent; VIP's ack validates every value as before.
export function parsedAckFields(parsed, cnDate) {
  const out = {};
  if (/^\d{4}-\d{2}-\d{2}$/.test(cnDate ?? "")) out.credit_note_date = `${cnDate}T00:00:00Z`;
  if (!parsed) return out;
  if (present(parsed.hsn_sac)) out.hsn_sac = parsed.hsn_sac;
  if (present(parsed.bill_to_address)) out.bill_to_address = parsed.bill_to_address;
  if (present(parsed.ship_to_address)) out.ship_to_address = parsed.ship_to_address;
  if (Number.isFinite(parsed.additional_charges)) out.additional_charges = parsed.additional_charges;
  if (parsed.irn_invoice === true) out.irn_invoice = true;
  if (present(parsed.irn_number)) out.irn_number = parsed.irn_number;
  return out;
}
